import React, { useEffect, useRef, useState } from 'react';
import PageLoader from './PageLoader';
import { loadGoogleMaps, onGoogleMapsAuthFailure } from '../lib/googleMapsLoader';
import { geoJsonToRing, MAX_ZONE_VERTICES, ringToGeoJson, validateZoneRing } from '../lib/sourcingZoneShape';

// Light basemap tuned to the research map palette. Street names stay visible
// because street context is the reason this level uses a street map.
export const REGION_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#f4f7f8' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 3 }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#cbd5e1' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#1e3a5f' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#eef4f2' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#e8eef2' }] },
  { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#d6e4e8' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#c3d4da' }] },
  { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#7b8a9b' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#cfe7e9' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3a9fa2' }] },
];

// A ZIP label is drawn only when its area is large enough on screen to hold
// it, so dense metros stay readable and labels appear as the user zooms in.
const ZIP_LABEL_MIN = { width: 54, height: 22 };
const CITY_LABEL_MIN = { width: 104, height: 40 };

function extendBounds(bounds, coordinates) {
  if (!Array.isArray(coordinates)) return;
  if (typeof coordinates[0] === 'number') {
    bounds.extend({ lat: coordinates[1], lng: coordinates[0] });
    return;
  }
  coordinates.forEach((child) => extendBounds(bounds, child));
}

const DRAFT_SHAPE_STYLE = {
  strokeColor: '#0f766e', strokeOpacity: 1, strokeWeight: 2.2, fillColor: '#0f766e', fillOpacity: 0.12, zIndex: 10,
};
const ZONE_STATUS_STYLE = {
  draft: { strokeColor: '#b45309', fillColor: '#f59e0b' },
  active: { strokeColor: '#0f766e', fillColor: '#0f766e' },
  paused: { strokeColor: '#64748b', fillColor: '#94a3b8' },
};

function zoneStyle(zone, selected) {
  const colors = ZONE_STATUS_STYLE[zone.status] || ZONE_STATUS_STYLE.draft;
  return {
    ...colors,
    strokeOpacity: 0.95,
    strokeWeight: selected ? 3 : 2,
    fillOpacity: selected ? 0.16 : 0.08,
    zIndex: selected ? 9 : 6,
  };
}

function censusBlockStyle(feature, selectedIds) {
  const geoid = String(feature.getProperty('GEOID') || feature.getId());
  const evidenceTotal = Number(feature.getProperty('evidence_total') || 0);
  const isSelected = selectedIds.has(geoid);
  return {
    clickable: true,
    fillColor: isSelected ? '#0f766e' : evidenceTotal ? '#2563eb' : '#ffffff',
    fillOpacity: isSelected ? 0.28 : evidenceTotal ? 0.12 : 0.025,
    strokeColor: isSelected ? '#0f766e' : evidenceTotal ? '#2563eb' : '#64748b',
    strokeOpacity: isSelected ? 1 : evidenceTotal ? 0.75 : 0.38,
    strokeWeight: isSelected ? 2.2 : evidenceTotal ? 1.3 : 0.7,
    zIndex: isSelected ? 9 : evidenceTotal ? 5 : 3,
  };
}

function polygonPoints(polygon) {
  return polygon.getPath().getArray().map((point) => ({ lat: point.lat(), lng: point.lng() }));
}

function propertyPhotoCarousel(point, title) {
  const urls = [...new Set((point.photo_urls || []).filter(Boolean))];
  if (!urls.length) return null;
  let index = 0;
  const frame = document.createElement('div');
  frame.className = 'fema-screening-map__evidence-photos';
  const image = document.createElement('img');
  image.loading = 'lazy';
  image.referrerPolicy = 'no-referrer';
  const counter = document.createElement('span');
  const renderPhoto = () => {
    image.src = urls[index];
    image.alt = `${title} photo ${index + 1} of ${urls.length}`;
    counter.textContent = `${index + 1} / ${urls.length}`;
  };
  frame.appendChild(image);
  if (urls.length > 1) {
    const previous = document.createElement('button');
    previous.type = 'button';
    previous.className = 'is-previous';
    previous.setAttribute('aria-label', 'Previous property photo');
    previous.textContent = '‹';
    previous.addEventListener('click', (event) => {
      event.stopPropagation();
      index = (index - 1 + urls.length) % urls.length;
      renderPhoto();
    });
    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'is-next';
    next.setAttribute('aria-label', 'Next property photo');
    next.textContent = '›';
    next.addEventListener('click', (event) => {
      event.stopPropagation();
      index = (index + 1) % urls.length;
      renderPhoto();
    });
    frame.append(previous, next);
  }
  frame.appendChild(counter);
  renderPhoto();
  return frame;
}

function zctaStyle(feature, { selectedKeys, stateAbbr, hoveredId, pinnedId, highlightedId, drawing, interactionsEnabled }) {
  const zcta5 = String(feature.getId());
  const selected = selectedKeys.has(`zcta:${feature.getProperty('state_abbr') || stateAbbr}:${zcta5}`);
  const emphasized = zcta5 === hoveredId || zcta5 === pinnedId || zcta5 === highlightedId;
  return {
    // While drawing, map clicks must reach the map to place corners.
    clickable: !drawing && interactionsEnabled,
    fillColor: '#0f766e',
    fillOpacity: selected ? 0.16 : emphasized ? 0.08 : 0,
    strokeColor: selected ? '#0f8179' : emphasized ? '#1e3a5f' : '#475569',
    strokeOpacity: selected || emphasized ? 0.95 : 0.55,
    strokeWeight: selected ? 2.2 : emphasized ? 1.8 : 0.9,
    zIndex: emphasized ? 3 : selected ? 2 : 1,
  };
}

export default function RegionGoogleMap({
  regionFeature,
  zctas,
  stateAbbr,
  selectedTargetKeys,
  hoveredZcta = null,
  pinnedZcta = null,
  highlightedZcta = null,
  focusRequest = null,
  countyFeatures = null,
  countyFills = {},
  showCountyLayer = false,
  zipInteractionsEnabled = true,
  evidencePoints = [],
  boundaryExperiment = null,
  boundaryExperimentMode = '',
  censusBlocks = null,
  censusBlockMode = false,
  selectedCensusBlockIds = [],
  onToggleCensusBlock,
  dimensions,
  label,
  onZctaHover,
  onZctaLeave,
  onZctaClick,
  onZctaFocused,
  onPinnedAnchorChange,
  onBackgroundClick,
  onViewportStart,
  onUnavailable,
  zones = [],
  zoneFocusRequest = null,
  onSaveZone,
  onUpdateZone,
  onDeleteZone,
}) {
  const containerRef = useRef(null);
  const mapStateRef = useRef(null);
  const handlersRef = useRef({});
  const styleStateRef = useRef({});
  const countyStateRef = useRef({});
  const [ready, setReady] = useState(false);
  const [zoneUi, setZoneUi] = useState({
    mode: 'idle', count: 0, validation: null, selectedZoneId: null, name: '', error: '', saving: false, confirmDelete: false,
  });
  const zoneUiRef = useRef(zoneUi);
  zoneUiRef.current = zoneUi;
  const draftPolygonRef = useRef(null);
  const zonePolygonsRef = useRef(new Map());
  const editOriginalRef = useRef(null);
  const evidenceMarkersRef = useRef([]);
  const boundaryLayersRef = useRef([]);
  const censusBlockLayerRef = useRef(null);
  const selectedCensusBlockIdsRef = useRef(selectedCensusBlockIds);
  selectedCensusBlockIdsRef.current = selectedCensusBlockIds;
  const drawingEnabled = typeof onSaveZone === 'function';

  handlersRef.current = {
    onZctaHover, onZctaLeave, onZctaClick, onZctaFocused, onPinnedAnchorChange,
    onBackgroundClick, onViewportStart, onUnavailable, onToggleCensusBlock,
  };
  styleStateRef.current = {
    selectedKeys: selectedTargetKeys, stateAbbr, hoveredId: hoveredZcta, pinnedId: pinnedZcta, highlightedId: highlightedZcta,
    drawing: zoneUi.mode === 'drawing', interactionsEnabled: zipInteractionsEnabled,
  };
  countyStateRef.current = { fills: countyFills, visible: showCountyLayer };

  useEffect(() => {
    let cancelled = false;
    let cleanup = () => {};
    const stopAuthWatch = onGoogleMapsAuthFailure((error) => {
      if (!cancelled) handlersRef.current.onUnavailable?.(error.message);
    });

    loadGoogleMaps().then((maps) => {
      const element = containerRef.current;
      if (cancelled || !element) return;
      const map = new maps.Map(element, {
        backgroundColor: '#f4f7f8',
        clickableIcons: false,
        disableDefaultUI: true,
        gestureHandling: 'cooperative',
        keyboardShortcuts: false,
        maxZoom: 17,
        minZoom: 7,
        styles: REGION_MAP_STYLES,
        zoomControl: true,
        zoomControlOptions: { position: maps.ControlPosition.RIGHT_BOTTOM },
      });

      // Per-ZIP bounds drive label fit, focus zoom, and centroid anchors.
      const zipInfo = new Map();
      (zctas || []).forEach((shape) => {
        if (!shape?.feature?.geometry) return;
        const bounds = new maps.LatLngBounds();
        extendBounds(bounds, shape.feature.geometry.coordinates);
        zipInfo.set(String(shape.geoId), {
          bounds,
          latitude: Number(shape.properties?.centroid_latitude),
          longitude: Number(shape.properties?.centroid_longitude),
          city: shape.properties?.primary_locality || '',
        });
      });

      // One overlay provides the projection for tooltip anchors and hosts
      // the ZIP/city labels in the same visual language as the SVG map.
      const labelLayer = document.createElement('div');
      labelLayer.className = 'fema-screening-map__google-labels';
      const labelElements = new Map();
      zipInfo.forEach((info, zcta5) => {
        const labelElement = document.createElement('div');
        labelElement.className = 'fema-screening-map__google-label';
        const cityElement = document.createElement('span');
        cityElement.textContent = info.city;
        const zipElement = document.createElement('b');
        zipElement.textContent = zcta5;
        labelElement.append(cityElement, zipElement);
        labelLayer.appendChild(labelElement);
        labelElements.set(zcta5, { labelElement, cityElement });
      });
      const overlay = new maps.OverlayView();
      overlay.onAdd = () => overlay.getPanes?.()?.overlayLayer?.appendChild(labelLayer);
      overlay.onRemove = () => labelLayer.remove();
      overlay.draw = () => {
        const projection = overlay.getProjection();
        if (!projection?.fromLatLngToDivPixel) return;
        zipInfo.forEach((info, zcta5) => {
          const { labelElement, cityElement } = labelElements.get(zcta5);
          const southWest = projection.fromLatLngToDivPixel(info.bounds.getSouthWest());
          const northEast = projection.fromLatLngToDivPixel(info.bounds.getNorthEast());
          const center = Number.isFinite(info.latitude) && Number.isFinite(info.longitude)
            ? projection.fromLatLngToDivPixel(new maps.LatLng(info.latitude, info.longitude))
            : null;
          const width = Math.abs(northEast.x - southWest.x);
          const height = Math.abs(southWest.y - northEast.y);
          const showZip = Boolean(center) && width >= ZIP_LABEL_MIN.width && height >= ZIP_LABEL_MIN.height;
          labelElement.hidden = !showZip;
          cityElement.hidden = !info.city || width < CITY_LABEL_MIN.width || height < CITY_LABEL_MIN.height;
          if (showZip) {
            labelElement.style.left = `${center.x}px`;
            labelElement.style.top = `${center.y}px`;
          }
        });
      };
      overlay.setMap(map);

      const anchorFor = (zcta5) => {
        const info = zipInfo.get(String(zcta5));
        const projection = overlay.getProjection();
        if (!info || !projection || !Number.isFinite(info.latitude) || !Number.isFinite(info.longitude)) return null;
        const point = projection.fromLatLngToContainerPixel(new maps.LatLng(info.latitude, info.longitude));
        const width = element.clientWidth;
        const height = element.clientHeight;
        if (!point || !width || !height) return null;
        // A pinned ZIP scrolled out of view hides its tooltip until it returns.
        if (point.x < 0 || point.y < 0 || point.x > width || point.y > height) return null;
        return {
          x: (point.x / width) * dimensions.width,
          y: (point.y / height) * dimensions.height,
        };
      };

      const countyLayer = new maps.Data({ map });
      if (countyFeatures?.features?.length) countyLayer.addGeoJson(countyFeatures);
      const styleCounties = () => countyLayer.setStyle((feature) => {
        const { fills, visible } = countyStateRef.current;
        return {
          clickable: false,
          visible,
          fillColor: fills[String(feature.getId())] || '#e2e8f0',
          fillOpacity: 0.42,
          strokeColor: '#94a3b8',
          strokeOpacity: 0.7,
          strokeWeight: 0.8,
          zIndex: 0,
        };
      });
      styleCounties();

      const zctaLayer = new maps.Data({ map });
      zipInfo.forEach((info, zcta5) => {
        const shape = zctas.find((item) => String(item.geoId) === zcta5);
        zctaLayer.addGeoJson({
          type: 'Feature',
          id: zcta5,
          properties: { ...shape.properties, zcta5 },
          geometry: shape.feature.geometry,
        });
      });
      const styleZctas = () => zctaLayer.setStyle((feature) => zctaStyle(feature, styleStateRef.current));
      styleZctas();
      zctaLayer.addListener('mouseover', (event) => {
        if (!styleStateRef.current.interactionsEnabled) return;
        const zcta5 = String(event.feature.getId());
        handlersRef.current.onZctaHover?.(zcta5, anchorFor(zcta5));
      });
      zctaLayer.addListener('mouseout', (event) => {
        if (!styleStateRef.current.interactionsEnabled) return;
        handlersRef.current.onZctaLeave?.(String(event.feature.getId()));
      });
      zctaLayer.addListener('click', (event) => {
        if (!styleStateRef.current.interactionsEnabled) return;
        event.domEvent?.stopPropagation?.();
        const zcta5 = String(event.feature.getId());
        handlersRef.current.onZctaClick?.(zcta5, anchorFor(zcta5));
      });

      const outlineLayer = new maps.Data({ map });
      if (regionFeature?.geometry) {
        outlineLayer.addGeoJson({ type: 'Feature', properties: {}, geometry: regionFeature.geometry });
      }
      outlineLayer.setStyle({
        clickable: false,
        fillOpacity: 0,
        strokeColor: '#1e3a5f',
        strokeOpacity: 0.95,
        strokeWeight: 2.6,
        zIndex: 4,
      });

      const regionBounds = new maps.LatLngBounds();
      extendBounds(regionBounds, regionFeature?.geometry?.coordinates);
      if (!regionBounds.isEmpty()) map.fitBounds(regionBounds, 24);

      let anchorFrame = null;
      const reportPinnedAnchor = () => {
        if (anchorFrame != null) return;
        const schedule = window.requestAnimationFrame || ((callback) => setTimeout(callback, 16));
        anchorFrame = schedule(() => {
          anchorFrame = null;
          const pinnedId = styleStateRef.current.pinnedId;
          if (pinnedId) handlersRef.current.onPinnedAnchorChange?.(pinnedId, anchorFor(pinnedId));
        });
      };
      const mapListeners = [
        map.addListener('click', (event) => {
          if (zoneUiRef.current.mode === 'drawing' && event?.latLng) {
            draftPolygonRef.current?.getPath().push(event.latLng);
            return;
          }
          handlersRef.current.onBackgroundClick?.();
        }),
        map.addListener('dragstart', () => handlersRef.current.onViewportStart?.()),
        map.addListener('bounds_changed', reportPinnedAnchor),
      ];

      mapStateRef.current = {
        maps, map, zipInfo, anchorFor, styleZctas, styleCounties,
      };
      setReady(true);

      cleanup = () => {
        mapListeners.forEach((listener) => listener.remove());
        maps.event.clearInstanceListeners(zctaLayer);
        [zctaLayer, outlineLayer, countyLayer].forEach((layer) => layer.setMap(null));
        overlay.setMap(null);
        mapStateRef.current = null;
      };
    }).catch((error) => {
      if (!cancelled) handlersRef.current.onUnavailable?.(error?.message || 'Could not load Google Maps.');
    });

    return () => {
      cancelled = true;
      stopAuthWatch();
      cleanup();
    };
    // The parent remounts this component for each region; geometry is fixed per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    mapStateRef.current?.styleZctas();
  }, [selectedTargetKeys, stateAbbr, hoveredZcta, pinnedZcta, highlightedZcta, zoneUi.mode, zipInteractionsEnabled, ready]);

  useEffect(() => {
    mapStateRef.current?.styleCounties();
  }, [countyFills, showCountyLayer, ready]);

  useEffect(() => {
    const state = mapStateRef.current;
    evidenceMarkersRef.current.forEach((marker) => marker.setMap(null));
    evidenceMarkersRef.current = [];
    if (!state || !evidencePoints.length) return undefined;
    const infoWindow = new state.maps.InfoWindow();
    evidenceMarkersRef.current = evidencePoints.flatMap((point) => {
      const latitude = Number(point.latitude);
      const longitude = Number(point.longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return [];
      const sold = point.evidence_type === 'sold';
      const rental = point.evidence_type === 'rentals';
      const marker = new state.maps.Marker({
        map: state.map,
        position: { lat: latitude, lng: longitude },
        title: point.formatted_address || point.street_address || (sold ? 'Sold comp' : rental ? 'Rental comp' : 'For-sale lead'),
        icon: {
          path: state.maps.SymbolPath.CIRCLE,
          scale: sold ? 6 : rental ? 5.5 : 5,
          fillColor: sold ? '#f59e0b' : rental ? '#2563eb' : '#0f766e',
          fillOpacity: 0.9,
          strokeColor: '#ffffff',
          strokeOpacity: 1,
          strokeWeight: 1.5,
        },
        zIndex: sold ? 12 : rental ? 11 : 10,
      });
      marker.addListener('click', () => {
        const content = document.createElement('div');
        content.className = 'fema-screening-map__evidence-card';
        const heading = document.createElement('a');
        heading.className = 'fema-screening-map__evidence-address';
        heading.href = `https://www.google.com/search?q=${encodeURIComponent(marker.getTitle())}`;
        heading.target = '_blank';
        heading.rel = 'noopener noreferrer';
        heading.setAttribute('aria-label', `Search Google for ${marker.getTitle()}`);
        heading.textContent = marker.getTitle();
        const value = document.createElement('b');
        const amount = sold ? point.sold_price : rental ? point.asking_rent_monthly : point.asking_price;
        value.textContent = amount == null
          ? (sold ? 'Sold price unavailable' : rental ? 'Asking rent unavailable' : 'Asking price unavailable')
          : `${sold ? 'Sold' : rental ? 'Rent' : 'Listed'} · $${Number(amount).toLocaleString()}${rental ? '/mo' : ''}`;
        const facts = document.createElement('span');
        const beds = point.bedrooms == null ? '—' : Number(point.bedrooms).toLocaleString();
        const baths = point.bathrooms == null ? '—' : Number(point.bathrooms).toLocaleString();
        const sqftValue = point.living_area_sqft ?? point.square_footage;
        const sqft = sqftValue == null ? '—' : Number(sqftValue).toLocaleString();
        facts.textContent = `${beds} bd · ${baths} ba · ${sqft} sq. ft.${sold && point.sold_date ? ` · ${point.sold_date}` : ''}`;
        const photos = propertyPhotoCarousel(point, marker.getTitle());
        if (photos) content.appendChild(photos);
        content.append(heading, value, facts);
        infoWindow.setContent(content);
        infoWindow.open({ map: state.map, anchor: marker });
      });
      return [marker];
    });
    return () => {
      infoWindow.close();
      evidenceMarkersRef.current.forEach((marker) => marker.setMap(null));
      evidenceMarkersRef.current = [];
    };
  }, [evidencePoints, ready]);

  useEffect(() => {
    boundaryLayersRef.current.forEach((layer) => layer.setMap(null));
    boundaryLayersRef.current = [];
    const state = mapStateRef.current;
    if (!state || !boundaryExperiment || !boundaryExperimentMode) return undefined;
    const modes = boundaryExperimentMode === 'compare'
      ? ['geoapify', 'overture']
      : [boundaryExperimentMode];
    modes.forEach((source) => {
      const collection = boundaryExperiment?.source_payloads?.[source]?.features;
      if (!collection?.features?.length) return;
      const layer = new state.maps.Data({ map: state.map });
      layer.addGeoJson(collection);
      layer.setStyle({
        clickable: true,
        fillColor: source === 'geoapify' ? '#2563eb' : '#7c3aed',
        fillOpacity: boundaryExperimentMode === 'compare' ? 0.08 : 0.12,
        strokeColor: source === 'geoapify' ? '#1d4ed8' : '#6d28d9',
        strokeOpacity: 0.9,
        strokeWeight: 2,
        zIndex: source === 'geoapify' ? 7 : 8,
      });
      const info = new state.maps.InfoWindow();
      layer.addListener('click', (event) => {
        const name = event.feature.getProperty('name') || 'Unnamed area';
        const type = event.feature.getProperty('boundary_type') || 'area';
        const card = document.createElement('div');
        card.className = 'fema-screening-map__boundary-card';
        const heading = document.createElement('strong');
        heading.textContent = name;
        const sourceLine = document.createElement('span');
        sourceLine.textContent = `${source === 'geoapify' ? 'Geoapify' : 'Overture'} · ${type}`;
        const warning = document.createElement('small');
        warning.textContent = 'Draft evidence only — not an approved comp zone.';
        card.append(heading, sourceLine, warning);
        info.setContent(card);
        info.setPosition(event.latLng);
        info.open({ map: state.map });
      });
      boundaryLayersRef.current.push(layer);
    });
    return () => {
      boundaryLayersRef.current.forEach((layer) => layer.setMap(null));
      boundaryLayersRef.current = [];
    };
  }, [boundaryExperiment, boundaryExperimentMode, ready]);

  useEffect(() => {
    censusBlockLayerRef.current?.setMap(null);
    censusBlockLayerRef.current = null;
    const state = mapStateRef.current;
    if (!state || !censusBlockMode || !censusBlocks?.features?.length) return undefined;
    const layer = new state.maps.Data({ map: state.map });
    layer.addGeoJson(censusBlocks, { idPropertyName: 'GEOID' });
    const selected = new Set(selectedCensusBlockIdsRef.current.map(String));
    layer.setStyle((feature) => censusBlockStyle(feature, selected));
    const info = new state.maps.InfoWindow();
    layer.addListener('click', (event) => {
      const geoid = String(event.feature.getProperty('GEOID') || event.feature.getId());
      handlersRef.current.onToggleCensusBlock?.(geoid);
      const counts = event.feature.getProperty('evidence_counts') || {};
      const card = document.createElement('div');
      card.className = 'fema-screening-map__boundary-card';
      const heading = document.createElement('strong');
      heading.textContent = `Census block ${event.feature.getProperty('BASENAME') || geoid.slice(-4)}`;
      const evidence = document.createElement('span');
      evidence.textContent = `${counts.for_sale || 0} leads · ${counts.sold || 0} sold · ${counts.rentals || 0} rentals`;
      const warning = document.createElement('small');
      warning.textContent = 'Building piece only — verify market similarity before saving.';
      card.append(heading, evidence, warning);
      info.setContent(card);
      info.setPosition(event.latLng);
      info.open({ map: state.map });
    });
    censusBlockLayerRef.current = layer;
    return () => {
      info.close();
      layer.setMap(null);
      if (censusBlockLayerRef.current === layer) censusBlockLayerRef.current = null;
    };
  }, [censusBlocks, censusBlockMode, ready]);

  useEffect(() => {
    const layer = censusBlockLayerRef.current;
    if (!layer) return;
    const selected = new Set(selectedCensusBlockIds.map(String));
    layer.setStyle((feature) => censusBlockStyle(feature, selected));
  }, [selectedCensusBlockIds]);

  // A table row click zooms to that ZIP, then reports its anchor so the
  // existing tooltip can pin once the camera settles.
  useEffect(() => {
    const state = mapStateRef.current;
    const info = focusRequest ? state?.zipInfo.get(String(focusRequest.zcta5)) : null;
    if (!state || !info) return undefined;
    const listener = state.maps.event.addListenerOnce(state.map, 'idle', () => {
      handlersRef.current.onZctaFocused?.(String(focusRequest.zcta5), state.anchorFor(focusRequest.zcta5));
    });
    state.map.fitBounds(info.bounds, 72);
    return () => listener?.remove?.();
  }, [focusRequest, ready]);

  const updateZoneUi = (patch) => setZoneUi((current) => ({ ...current, ...patch }));

  const watchShape = (polygon) => {
    const refresh = () => {
      const validation = validateZoneRing(polygonPoints(polygon));
      updateZoneUi({ count: validation.count, validation, error: '' });
    };
    const path = polygon.getPath();
    ['insert_at', 'remove_at', 'set_at'].forEach((name) => path.addListener(name, refresh));
    refresh();
  };

  const setDrawingCursor = (drawing) => {
    mapStateRef.current?.map.setOptions({
      draggableCursor: drawing ? 'crosshair' : null,
      disableDoubleClickZoom: drawing,
    });
  };

  const setSavedZonesClickable = (clickable) => {
    zonePolygonsRef.current.forEach((polygon) => polygon.setOptions({ clickable }));
  };

  const clearDraft = () => {
    draftPolygonRef.current?.setMap(null);
    draftPolygonRef.current = null;
    setDrawingCursor(false);
    setSavedZonesClickable(true);
  };

  const startDrawing = () => {
    const state = mapStateRef.current;
    if (!state) return;
    handlersRef.current.onBackgroundClick?.();
    const polygon = new state.maps.Polygon({ ...DRAFT_SHAPE_STYLE, map: state.map, editable: true, clickable: true });
    // Google creates no path for an empty polygon; give it one to append corners to.
    polygon.setPath(new state.maps.MVCArray());
    // Clicks inside the unfinished shape still place corners; the first
    // corner closes it.
    polygon.addListener('click', (event) => {
      if (zoneUiRef.current.mode !== 'drawing') return;
      if (event?.vertex === 0 && polygon.getPath().getLength() >= 3) {
        finishDrawing();
        return;
      }
      if (event?.vertex == null && event?.latLng) polygon.getPath().push(event.latLng);
    });
    draftPolygonRef.current = polygon;
    watchShape(polygon);
    setDrawingCursor(true);
    setSavedZonesClickable(false);
    updateZoneUi({ mode: 'drawing', selectedZoneId: null, name: '', error: '', confirmDelete: false });
  };

  const undoCorner = () => {
    const path = draftPolygonRef.current?.getPath();
    if (path?.getLength()) path.pop();
  };

  function finishDrawing() {
    const polygon = draftPolygonRef.current;
    if (!polygon) return;
    const validation = validateZoneRing(polygonPoints(polygon));
    if (!validation.valid) {
      updateZoneUi({ validation, count: validation.count });
      return;
    }
    setDrawingCursor(false);
    updateZoneUi({ mode: 'naming', validation, count: validation.count });
  }

  const cancelDrawing = () => {
    clearDraft();
    updateZoneUi({ mode: 'idle', count: 0, validation: null, name: '', error: '' });
  };

  const saveDraft = async () => {
    const polygon = draftPolygonRef.current;
    const name = zoneUi.name.trim();
    if (!polygon || !name) {
      updateZoneUi({ error: 'Name this area before saving.' });
      return;
    }
    const validation = validateZoneRing(polygonPoints(polygon));
    if (!validation.valid) {
      updateZoneUi({ validation, error: validation.message });
      return;
    }
    updateZoneUi({ saving: true, error: '' });
    try {
      await onSaveZone(name, ringToGeoJson(polygonPoints(polygon)));
      clearDraft();
      updateZoneUi({ mode: 'idle', count: 0, validation: null, name: '', saving: false });
    } catch (error) {
      updateZoneUi({ saving: false, error: error?.message || 'Could not save this area.' });
    }
  };

  const selectedZone = zones.find((zone) => String(zone.zone_id) === String(zoneUi.selectedZoneId)) || null;

  const editZoneShape = () => {
    const polygon = zonePolygonsRef.current.get(String(zoneUi.selectedZoneId));
    if (!polygon) return;
    editOriginalRef.current = polygonPoints(polygon);
    polygon.setEditable(true);
    watchShape(polygon);
    updateZoneUi({ mode: 'zone-editing', confirmDelete: false });
  };

  const cancelZoneEdit = () => {
    const polygon = zonePolygonsRef.current.get(String(zoneUi.selectedZoneId));
    if (polygon && editOriginalRef.current) {
      polygon.setEditable(false);
      polygon.setPaths(editOriginalRef.current);
    }
    updateZoneUi({ mode: 'zone-selected', error: '', validation: null });
  };

  const saveZoneShape = async () => {
    const polygon = zonePolygonsRef.current.get(String(zoneUi.selectedZoneId));
    if (!polygon) return;
    const validation = validateZoneRing(polygonPoints(polygon));
    if (!validation.valid) {
      updateZoneUi({ validation, error: validation.message });
      return;
    }
    updateZoneUi({ saving: true, error: '' });
    try {
      await onUpdateZone(zoneUi.selectedZoneId, ringToGeoJson(polygonPoints(polygon)));
      polygon.setEditable(false);
      updateZoneUi({ mode: 'zone-selected', saving: false, validation: null });
    } catch (error) {
      updateZoneUi({ saving: false, error: error?.message || 'Could not update this area.' });
    }
  };

  const deleteZone = async () => {
    updateZoneUi({ saving: true, error: '' });
    try {
      await onDeleteZone(zoneUi.selectedZoneId);
      updateZoneUi({ mode: 'idle', selectedZoneId: null, confirmDelete: false, saving: false });
    } catch (error) {
      updateZoneUi({ saving: false, confirmDelete: false, error: error?.message || 'Could not delete this area.' });
    }
  };

  const focusZone = (zoneId) => {
    const state = mapStateRef.current;
    const polygon = zonePolygonsRef.current.get(String(zoneId));
    if (!state || !polygon) return;
    const bounds = new state.maps.LatLngBounds();
    polygonPoints(polygon).forEach((point) => bounds.extend(point));
    if (!bounds.isEmpty()) state.map.fitBounds(bounds, 72);
  };

  // Saved areas render as their own polygons so one can be edited in place.
  useEffect(() => {
    const state = mapStateRef.current;
    if (!state) return undefined;
    const polygons = zonePolygonsRef.current;
    const liveIds = new Set(zones.map((zone) => String(zone.zone_id)));
    polygons.forEach((polygon, zoneId) => {
      if (!liveIds.has(zoneId)) {
        polygon.setMap(null);
        polygons.delete(zoneId);
      }
    });
    zones.forEach((zone) => {
      const zoneId = String(zone.zone_id);
      const selected = zoneId === String(zoneUiRef.current.selectedZoneId);
      let polygon = polygons.get(zoneId);
      if (!polygon) {
        polygon = new state.maps.Polygon({ map: state.map, clickable: true });
        polygon.addListener('click', () => {
          const { mode } = zoneUiRef.current;
          if (mode !== 'idle' && mode !== 'zone-selected') return;
          handlersRef.current.onBackgroundClick?.();
          setZoneUi((current) => ({
            ...current, mode: 'zone-selected', selectedZoneId: zoneId, confirmDelete: false, error: '',
          }));
        });
        polygons.set(zoneId, polygon);
      }
      if (!(zoneUiRef.current.mode === 'zone-editing' && selected)) {
        polygon.setPaths(geoJsonToRing(zone.geometry_geojson));
        polygon.setEditable(false);
      }
      polygon.setOptions(zoneStyle(zone, selected));
    });
    return undefined;
  }, [zones, zoneUi.selectedZoneId, ready]);

  useEffect(() => () => {
    zonePolygonsRef.current.forEach((polygon) => polygon.setMap(null));
    zonePolygonsRef.current.clear();
    draftPolygonRef.current?.setMap(null);
    evidenceMarkersRef.current.forEach((marker) => marker.setMap(null));
    evidenceMarkersRef.current = [];
    censusBlockLayerRef.current?.setMap(null);
    censusBlockLayerRef.current = null;
  }, []);

  useEffect(() => {
    if (!zoneFocusRequest || !mapStateRef.current) return;
    focusZone(zoneFocusRequest.zoneId);
    setZoneUi((current) => (current.mode === 'idle' || current.mode === 'zone-selected'
      ? { ...current, mode: 'zone-selected', selectedZoneId: String(zoneFocusRequest.zoneId), confirmDelete: false, error: '' }
      : current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneFocusRequest, ready]);

  const { mode } = zoneUi;
  const countLabel = `${zoneUi.count}/${MAX_ZONE_VERTICES} corners`;
  const shapeProblem = zoneUi.validation && !zoneUi.validation.valid && zoneUi.count >= 3 ? zoneUi.validation.message : '';

  return (
    <div className="fema-screening-map__google">
      <div ref={containerRef} className="fema-screening-map__google-canvas" role="img" aria-label={label} />
      {ready && drawingEnabled ? (
        <div
          className="fema-screening-map__draw-toolbar"
          role="toolbar"
          aria-label="Draw a sourcing area"
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              if (mode === 'drawing' || mode === 'naming') cancelDrawing();
              else if (mode === 'zone-editing') cancelZoneEdit();
            }
          }}
        >
          {mode === 'idle' ? (
            <button type="button" className="is-primary" onClick={startDrawing}>
              <i className="pi pi-pencil" aria-hidden="true" /> Draw area
            </button>
          ) : null}
          {mode === 'drawing' ? (
            <>
              <span className="fema-screening-map__draw-hint">Click the map to add corners</span>
              <b className="fema-screening-map__draw-count">{countLabel}</b>
              <button type="button" onClick={undoCorner} disabled={!zoneUi.count}>Undo</button>
              <button type="button" className="is-primary" onClick={finishDrawing} disabled={zoneUi.count < 3}>Finish</button>
              <button type="button" onClick={cancelDrawing}>Cancel</button>
            </>
          ) : null}
          {mode === 'naming' ? (
            <>
              <input
                type="text"
                value={zoneUi.name}
                maxLength={120}
                placeholder="Area name"
                aria-label="Area name"
                autoFocus
                onChange={(event) => updateZoneUi({ name: event.target.value, error: '' })}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') saveDraft();
                }}
              />
              <b className="fema-screening-map__draw-count">{countLabel}</b>
              <button type="button" className="is-primary" onClick={saveDraft} disabled={zoneUi.saving || !zoneUi.name.trim()}>
                {zoneUi.saving ? 'Saving…' : 'Save draft'}
              </button>
              <button type="button" onClick={cancelDrawing} disabled={zoneUi.saving}>Discard</button>
            </>
          ) : null}
          {mode === 'zone-selected' && selectedZone ? (
            zoneUi.confirmDelete ? (
              <>
                <span className="fema-screening-map__draw-hint">Delete <b>{selectedZone.name}</b>?</span>
                <button type="button" className="is-danger" onClick={deleteZone} disabled={zoneUi.saving}>Delete</button>
                <button type="button" onClick={() => updateZoneUi({ confirmDelete: false })}>Keep</button>
              </>
            ) : (
              <>
                <strong className="fema-screening-map__draw-name" title={selectedZone.name}>{selectedZone.name}</strong>
                <span className={`fema-screening-map__zone-status is-${selectedZone.status}`}>{selectedZone.status}</span>
                <button type="button" onClick={editZoneShape}>Edit shape</button>
                {selectedZone.status === 'draft' && !selectedZone.validated_at ? (
                  <button type="button" className="is-danger" onClick={() => updateZoneUi({ confirmDelete: true })}>Delete</button>
                ) : null}
                <button type="button" aria-label="Close area actions" onClick={() => updateZoneUi({ mode: 'idle', selectedZoneId: null })}>
                  <i className="pi pi-times" aria-hidden="true" />
                </button>
              </>
            )
          ) : null}
          {mode === 'zone-editing' ? (
            <>
              <span className="fema-screening-map__draw-hint">
                Drag corners; drag a midpoint to add one{selectedZone && selectedZone.status !== 'draft' ? ' · saving returns it to draft' : ''}
              </span>
              <b className="fema-screening-map__draw-count">{countLabel}</b>
              <button type="button" className="is-primary" onClick={saveZoneShape} disabled={zoneUi.saving}>
                {zoneUi.saving ? 'Saving…' : 'Save shape'}
              </button>
              <button type="button" onClick={cancelZoneEdit} disabled={zoneUi.saving}>Cancel</button>
            </>
          ) : null}
          {shapeProblem || zoneUi.error ? (
            <span className="fema-screening-map__draw-error" role="alert">{zoneUi.error || shapeProblem}</span>
          ) : null}
          {mode === 'naming' && !zoneUi.error ? (
            <span className="fema-screening-map__draw-note">Saved as a draft · no credits used</span>
          ) : null}
        </div>
      ) : null}
      {!ready ? (
        <div className="fema-screening-map__google-loading">
          <PageLoader compact label="Loading region street map" />
        </div>
      ) : null}
    </div>
  );
}
